import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import BtnLinkedIn from "./btn-lin";
import BtnMail from "./btn-mail";
import { motion } from "framer-motion";

interface AboutPageProps {
    onClose: () => void;
}

export default function AboutPage({ onClose }: AboutPageProps) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="h-dvh w-screen flex justify-center absolute top-0 left-0 z-200 bg-background backdrop-blur-lg"
        >
            <ScrollArea className="w-full h-full flex-1">

                <div className="pt-40 pb-50 px-6 md:px-4 w-full max-w-4xl mx-auto min-h-dvh">

                    <h1 className="text-4xl font-bold mb-6 font-heading">About David</h1>

                    <div className="space-y-5 leading-relaxed text-muted-foreground">

                        <p>I am a Product & Technology Executive who bridges the gap between business strategy and engineering reality.

                            With over 25 years of experience, I lead teams across creative technology, fintech, and large-scale digital systems. I work at the intersection of product thinking, engineering execution, and design judgment helping organizations <span className="text-foreground">translate complex technical capabilities into coherent business value.</span>.</p>

                        <p>My work has ranged from building interactive digital experiences and creative platforms to designing and operating data-intensive systems, financial tooling, and production infrastructure. What connects it all is a focus on turning ideas into <span className="text-foreground">coherent, working systems</span> and aligning vision, technology, and execution in one direction.</p>

                        <p>This site is meant to be a <span className="text-foreground">practical entry point</span> into who I am, what I&apos;ve done, and how I think. Instead of presenting a static résumé or a curated highlight reel, it lets you explore my background through conversation.</p>

                        <hr />

                        <h3 className="text-2xl font-semibold text-foreground font-heading">Architecture & Engineering Strategy</h3>

                        <p>The platform itself is intentionally simple in structure, but deliberate in its choices.</p>

                        <p>
                            <span className="font-bold text-foreground">Frontend & Backend</span><br />
                            - Next.js<br />
                            - Fastify / PostgreSQL<br />
                            <br />

                            <span className="font-bold text-foreground">Infrastructure & CI/CD</span><br />
                            - Docker Compose<br />
                            - GitHub Actions<br />
                            - AWS (EC2/ECR)<br />
                            <br />
                            <span className="font-bold text-foreground">LLM integration</span><br />
                            - Google Gemini 3 Flash Preview (inference)
                        </p>

                        <p>I deliberately chose <span className="font-bold text-foreground">not</span> to implement a custom RAG pipeline. The corpus is small enough to fit comfortably into Gemini&apos;s request context, and for this use case the added complexity of vector databases, retrieval tuning, and ranking heuristics would have outweighed the benefits.</p>

                        <p>Similarly, the infrastructure eschews complex orchestration for a reliable, containerized setup. It&apos;s a standard Docker Compose stack, deployed to AWS via GitHub Actions. Boring, stable, and easy to maintain. Simplicity won.</p>

                        <hr />

                        <h3 className="text-2xl font-semibold text-foreground">The Product Process: Curating Intelligence</h3>

                        <p>The most time-consuming part of this project wasn&apos;t the engineering. It was the content.</p>

                        <p>Making something that could genuinely answer meaningful questions about my work required more than a résumé rewrite. The workflow ended up being:</p>
                        - voice memos and free-form thinking<br />
                        - LLM-assisted transcription and cleanup<br />
                        - manual selection, rewriting, and refinement<br />
                        - LLM-assisted classification and tagging<br />
                        <br />
                        <p>That loop. Human judgment first, automation second, proved to be the only way to keep the content honest, specific, and useful.</p>

                        <hr />

                        <h3 className="text-2xl font-semibold text-foreground">Let&apos;s Solve the Hard Problems</h3>
                        <p>I hope this site works as a bite-sized entry point into my capabilities. But more than that, I&apos;m interested in the <span className="text-foreground italic">organizational or technical challenges you are facing right now.</span></p>

                        <p>Are you trying to scale a product?<br /> Align engineering with business goals?<br /> Or modernize a legacy stack without breaking it?</p>

                        <p>If you&apos;d rather talk directly, feel free to reach out via email or LinkedIn. I&apos;m always interested in hearing the real story behind the problem.</p>


                    </div>

                    <div className="mt-10 flex gap-2 items-center text-muted-foreground">
                        <div onClick={onClose} className="mr-4 pl-0 cursor-pointer hover:text-foreground transition-colors">
                            <ArrowLeftIcon className="inline-block mr-1" /> Back
                        </div>
                        <BtnLinkedIn />
                        <BtnMail />
                    </div>
                </div>
            </ScrollArea>
        </motion.div>
    );
}